import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Copy,
  Lock,
  MoreHorizontal,
  Pause,
  Play,
  Rocket,
  Settings2,
  Trash2,
} from "lucide-react";
import {
  deleteCampaign,
  duplicateCampaign,
  errorMessage,
  getCampaign,
  updateCampaign,
  type CampaignDetail,
  type CampaignStatus,
  type EmailMeta,
  type EmailProjectAccess,
} from "@/api/email";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useConfirm } from "@/components/ui/confirm-dialog";
import {
  CampaignStatusBadge,
  DryRunNotice,
  Hint,
  ProviderModeBadge,
  ScopeBadge,
} from "@/components/email/EmailBadges";
import { CampaignContactSheet } from "@/components/email/CampaignContactSheet";
import { CampaignSettingsDialog } from "@/components/email/CampaignSettingsDialog";
import { WorkspaceNav } from "@/components/email/WorkspaceNav";
import { SentFromLine } from "@/components/email/SenderField";
import { SyncButton, useCampaignSync } from "@/components/email/SyncControl";
import { useContactActions } from "@/components/email/useContactActions";
import { useEmailMeta } from "@/hooks/useEmailMeta";
import {
  PREVIOUS_TEAM_NOTE,
  canChangeCampaignState,
  canDeleteCampaign,
  deleteCampaignText,
  leadsInCampaign,
  plural,
  sendingDaysLabel,
  stepDays,
} from "@/lib/email";
import {
  REVIEW_TAB,
  WORKSPACE_TABS,
  type WorkspaceTab,
  campaignPath,
  campaignsPath,
  emailBase,
  workspaceIndexTarget,
  workspaceTabOf,
} from "@/lib/emailRoutes";
import { formatDateTime, formatNumber, formatRelativeDate } from "@/lib/formatters";
import { useEmailAccessGuard } from "@/pages/email/EmailLayout";

export interface WorkspaceContext {
  slug: string;
  campaign: CampaignDetail;
  setCampaign: (campaign: CampaignDetail) => void;
  /** Re-read the campaign (counts, steps, provider state). */
  reload: () => Promise<void>;
  meta: EmailMeta;
  access: EmailProjectAccess;
  external: boolean;
  /** Open a lead's drawer (`?lead=` — a deep link). */
  openLead: (membershipId: number) => void;
  /** Changes after any lead action: tabs reload their lists. */
  version: number;
  /** A lead or the campaign changed: reload the campaign, then the tabs. */
  changed: () => void;
  actions: ReturnType<typeof useContactActions>;
}

export function useWorkspace(): WorkspaceContext {
  return useOutletContext<WorkspaceContext>();
}

/**
 * One campaign: a stable header (name, status, project, provider, actions)
 * over its four tabs — Sequence, Leads, Launch, Performance — each in the
 * URL. A campaign is always opened in its own project.
 */
export default function CampaignWorkspace() {
  const { slug, campaignId } = useParams<{ slug: string; campaignId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [params, setParams] = useSearchParams();
  const allowed = useEmailAccessGuard(slug);
  const { meta, access } = useEmailMeta(slug);
  const id = Number(campaignId);

  const [campaign, setCampaign] = useState<CampaignDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [version, setVersion] = useState(0);

  const load = useCallback(async () => {
    if (!slug || !Number.isFinite(id)) return;
    try {
      setCampaign(await getCampaign(slug, id));
      setError(null);
    } catch (err) {
      setError(errorMessage(err, "This campaign could not be loaded."));
    }
  }, [slug, id]);

  useEffect(() => {
    setCampaign(null);
    void load();
  }, [load]);

  const external = !!meta?.external_effects;
  const changed = useCallback(() => {
    setVersion((n) => n + 1);
    void load();
  }, [load]);
  const actions = useContactActions({ external, onChanged: changed });
  const sync = useCampaignSync(
    campaign ?? ({ id, project_slug: slug, contacts_by_status: {}, status: "DRAFT" } as unknown as CampaignDetail),
    external,
    changed,
  );

  const leadParam = Number(params.get("lead"));
  const openLeadId = Number.isInteger(leadParam) && leadParam > 0 ? leadParam : null;
  const openLead = useCallback(
    (membershipId: number) => {
      const next = new URLSearchParams(params);
      next.set("lead", String(membershipId));
      setParams(next);
    },
    [params, setParams],
  );
  const closeLead = () => {
    const next = new URLSearchParams(params);
    next.delete("lead");
    setParams(next, { replace: true });
  };

  if (!allowed || !slug) return null;

  if (error) {
    return (
      <div className="space-y-4">
        <BackToCampaigns slug={slug} />
        <div className="flex items-center justify-center gap-2 rounded-plane bg-white p-8 text-sm text-rose-600 shadow-plane">
          <AlertTriangle className="h-4 w-4" /> {error}
        </div>
      </div>
    );
  }
  if (!campaign || !meta || !access) {
    return (
      <div className="space-y-4" aria-busy="true">
        <BackToCampaigns slug={slug} />
        <Skeleton className="h-36 w-full rounded-plane" />
        <Skeleton className="h-72 w-full rounded-plane" />
      </div>
    );
  }

  const tab = workspaceTabOf(location.pathname);
  const days = stepDays(campaign.steps);
  const lastDay = days.length ? days[days.length - 1] : 0;
  const canChangeState = canChangeCampaignState(access, campaign);
  const lock = campaign.execution_lock ?? null;
  const settings = campaign.settings;
  const keep = (extra?: Record<string, string>) => {
    // A tab switch keeps the open lead, nothing else of the tab's own state.
    const lead = params.get("lead");
    return { ...(lead ? { lead } : {}), ...extra };
  };

  async function setStatus(status: CampaignStatus) {
    if (!campaign) return;
    if (status === "ACTIVE" && campaign.status === "DRAFT") {
      const ok = await confirm({
        title: `Activate “${campaign.name}”?`,
        description: external
          ? `The campaign is created in Lemlist with its ${plural(campaign.steps.length, "step")}. Nothing is sent until you launch leads from the Launch tab; Lemlist then sends on the campaign's days and hours.`
          : "Dry run: the activation and the sync are simulated. Nothing is sent to anyone.",
        confirmLabel: external ? "Activate" : "Activate (dry run)",
        variant: external ? "warning" : "info",
      });
      if (!ok) return;
    }
    if (status === "ARCHIVED") {
      const ok = await confirm({
        title: `Archive “${campaign.name}”?`,
        description: `${
          external && campaign.provider_mode === "LIVE"
            ? "Lemlist stops sending."
            : external
              ? "Nothing changes in Lemlist: this campaign was never synced."
              : "Dry run: nothing reaches Lemlist."
        } Leads keep their history; you can restore the campaign later.`,
        confirmLabel: "Archive",
        variant: "warning",
      });
      if (!ok) return;
    }
    try {
      const updated = await updateCampaign(campaign.project_slug, campaign.id, { status });
      setCampaign(updated);
      toast.success(
        status === "ACTIVE" ? "Campaign active" : status === "PAUSED" ? "Campaign paused" : "Campaign archived",
      );
      if (status === "ACTIVE" && campaign.status === "DRAFT") {
        if (access?.can_sync) await sync.run();
        else toast.info("Active in Prism: someone allowed to sync hands it to Lemlist.");
      }
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  async function duplicate() {
    if (!campaign) return;
    try {
      const copy = await duplicateCampaign(campaign.project_slug, campaign.id);
      toast.success(`“${copy.name}” created as a draft`);
      navigate(campaignPath(copy.project_slug, copy.id));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  async function remove() {
    if (!campaign) return;
    const ok = await confirm({
      title: `Delete “${campaign.name}” for good?`,
      description: deleteCampaignText(campaign),
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      const result = await deleteCampaign(campaign.project_slug, campaign.id);
      toast.success(result.note ?? `“${result.name}” deleted`);
      navigate(campaignsPath(slug!));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  const byStatus = campaign.contacts_by_status;
  const inCampaign = leadsInCampaign(byStatus);
  const ready = byStatus.EMAIL_READY ?? 0;
  const launched = Math.max(inCampaign - ready, 0);
  const replied = byStatus.EMAIL_REPLIED ?? 0;
  const stepCount = campaign.steps.length;
  const pilotPeople = campaign.pilot ? (campaign.pilot.leads ?? campaign.pilot.scenarios ?? []).length : 0;
  const summaries: Record<WorkspaceTab, string> = {
    review: campaign.pilot?.kind === "PILOT"
      ? `${campaign.pilot.profile === "CFO_US_GROWTH_V1" ? "Lisan review" : "PPA review"} · ${plural(pilotPeople, "prospect")}`
      : `Not qualified · ${plural(pilotPeople, "scenario")}`,
    sequence: stepCount
      ? `${plural(stepCount, "email")}${stepCount > 1 ? ` over ${plural(lastDay, "day")}` : ""}`
      : "Write the first email",
    leads: lock
      ? `${formatNumber(inCampaign)} paused · never launched`
      : inCampaign
        ? `${formatNumber(launched)} launched · ${formatNumber(ready)} ready`
        : "Add the people to email",
    launch: lock
      ? "Locked: nothing can be launched"
      : ready
        ? `${plural(ready, "lead")} ready to launch`
        : launched
          ? "Every lead is launched"
          : "Nothing to launch yet",
    performance: lock
      ? "Nothing sent: nothing to measure"
      : replied
        ? `${plural(replied, "reply", "replies")} so far`
        : launched
          ? "Opens, clicks and replies"
          : "Results after the first launch",
  };
  const tabs = campaign.pilot ? [WORKSPACE_TABS[0], REVIEW_TAB, ...WORKSPACE_TABS.slice(1)] : WORKSPACE_TABS;
  const navItems = tabs.map((t) => ({
    key: t.key,
    label: t.label,
    to: campaignPath(slug, campaign.id, t.key, keep()),
    count: t.key === "leads" ? inCampaign : t.key === "launch" ? ready : undefined,
    summary: summaries[t.key],
    attention: t.key === "launch" && ready > 0,
  }));

  const context: WorkspaceContext = {
    slug,
    campaign,
    setCampaign,
    reload: load,
    meta,
    access,
    external,
    openLead,
    version,
    changed,
    actions,
  };

  return (
    <div className={tab === "review" ? "space-y-4" : "space-y-4 pb-24"}>
      <BackToCampaigns slug={slug} />

      <section className="rounded-plane bg-white px-5 py-4 shadow-plane">
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="min-w-[min(100%,26rem)] flex-1 space-y-1.5">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h1 className="truncate text-xl font-semibold tracking-tight text-slate-900">{lock ? campaign.name.replace(/ \| 10 (?:prospects|scénarios)$/, "") : campaign.name}</h1>
              {!lock && <><CampaignStatusBadge status={campaign.status} /><ProviderModeBadge mode={campaign.provider_mode} /></>}
              {lock && (
                <Hint content={lock.reason}>
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                    <Lock className="h-3 w-3" /> En pause
                  </span>
                </Hint>
              )}
              {campaign.provider_legacy && (
                <Hint content={PREVIOUS_TEAM_NOTE}>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                    Previous team
                  </span>
                </Hint>
              )}
            </div>
            {lock ? <p className="text-xs text-slate-500">
              Lemlist : {campaign.pilot_sync?.status === "COMPLETED" ? "brouillons synchronisés" : String(campaign.pilot_sync?.status) === "DIRTY" ? "modifications à synchroniser" : campaign.pilot_sync?.status ?? "non synchronisé"}
              {campaign.pilot?.kind !== "PILOT" && " · Démonstration non qualifiée, aucun destinataire"}
            </p> : <><div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <ScopeBadge scope={campaign.scope} />
                <span className="text-slate-600">{campaign.project_name}</span>
              </span>
              <span>
                {plural(campaign.steps.length, "step")}
                {campaign.steps.length > 1 && ` over ${plural(lastDay, "day")}`}
              </span>
              <span>
                {sendingDaysLabel(settings.sending_days)} {settings.sending_start_time}–{settings.sending_end_time}{" "}
                <span className="text-slate-400">({settings.timezone})</span>
              </span>
              <span>Daily limit {settings.daily_limit}</span>
              <span title={campaign.last_synced_at ? formatDateTime(campaign.last_synced_at) : undefined}>
                {campaign.last_synced_at ? `Synced ${formatRelativeDate(campaign.last_synced_at)}` : "Never synced"}
              </span>
              {campaign.last_provider_sync_at && (
                <Hint content="The Email Sync job reads Lemlist on its schedule (Engine Control): states, events, replies.">
                  <span title={formatDateTime(campaign.last_provider_sync_at)}>
                    Reconciled {formatRelativeDate(campaign.last_provider_sync_at)}
                    {campaign.provider_status ? ` (Lemlist: ${campaign.provider_status})` : ""}
                  </span>
                </Hint>
              )}
              {campaign.unsynced_changes && campaign.provider_mode !== "NONE" && (
                <span className="inline-flex items-center gap-1 font-medium text-amber-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> Unsynced changes
                </span>
              )}
            </div>
            <div className="text-xs [&_p]:text-xs">
              {lock ? (
                <p className="text-slate-500">No sending mailbox assigned · locked review pilot</p>
              ) : (
                <SentFromLine slug={campaign.project_slug} sender={settings.sender} canManage={access.can_manage} />
              )}
            </div></>}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {!external && <DryRunNotice compact />}
            {access.can_sync && campaign.status !== "ARCHIVED" && !campaign.provider_legacy && !lock && (
              <SyncButton
                running={sync.running}
                onClick={() => void sync.run()}
                disabled={!campaign.steps.length}
                external={external}
              />
            )}
            {access.can_manage && campaign.status === "DRAFT" && !campaign.provider_legacy && !lock && (
              <Hint content={campaign.steps.length ? null : "Add a first step to activate the campaign."}>
                <span>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={!campaign.steps.length || sync.running}
                    onClick={() => void setStatus("ACTIVE")}
                  >
                    <Rocket className="h-3.5 w-3.5" />
                    Activate
                  </Button>
                </span>
              </Hint>
            )}
            {canChangeState && campaign.status === "ACTIVE" && (
              <Button variant="outline" size="sm" onClick={() => void setStatus("PAUSED")}>
                <Pause className="h-3.5 w-3.5" />
                Pause
              </Button>
            )}
            {canChangeState && campaign.status === "PAUSED" && (
              <Button variant="primary" size="sm" onClick={() => void setStatus("ACTIVE")}>
                <Play className="h-3.5 w-3.5" />
                Resume
              </Button>
            )}
            {access.can_manage && !lock && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSettingsOpen(true)}
                disabled={campaign.status === "ARCHIVED" || campaign.provider_legacy || !!lock}
              >
                <Settings2 className="h-3.5 w-3.5" />
                Settings
              </Button>
            )}
            {access.can_manage && !lock && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500" aria-label="More actions">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  {access.can_manage && !lock && (
                    <DropdownMenuItem onClick={() => void duplicate()}>
                      <Copy className="h-4 w-4" /> Duplicate
                    </DropdownMenuItem>
                  )}
                  {canChangeState && <DropdownMenuSeparator />}
                  {!canChangeState ? null : campaign.status === "ARCHIVED" ? (
                    <DropdownMenuItem onClick={() => void setStatus("PAUSED")}>
                      <ArchiveRestore className="h-4 w-4" /> Restore (paused)
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      className="text-rose-600 focus:text-rose-700"
                      onClick={() => void setStatus("ARCHIVED")}
                    >
                      <Archive className="h-4 w-4" /> Archive
                    </DropdownMenuItem>
                  )}
                  {canDeleteCampaign(access, campaign) && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-rose-600 focus:text-rose-700" onClick={() => void remove()}>
                        <Trash2 className="h-4 w-4" /> Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {(campaign.sync_error || campaign.last_provider_error) && (
          <div className="mt-3 space-y-1.5">
            {campaign.sync_error && (
              <p className="flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-800">
                <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
                <span>
                  <span className="font-medium">Last sync: </span>
                  {campaign.sync_error}
                </span>
              </p>
            )}
            {campaign.last_provider_error && (
              <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
                <span>
                  <span className="font-medium">Last reconciliation with Lemlist: </span>
                  {campaign.last_provider_error}
                </span>
              </p>
            )}
          </div>
        )}

      </section>

      <WorkspaceNav value={tab} items={navItems} compact={!!lock} />

      <Outlet context={context} />

      <CampaignSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} campaign={campaign} onSaved={setCampaign} />
      <CampaignContactSheet
        slug={slug}
        meta={meta}
        membershipId={openLeadId}
        onOpenChange={(open) => !open && closeLead()}
        onAction={
          lock
            ? undefined
            : async (action, row) => {
                if (action === "move") await actions.move([row]);
                else await actions.act(action, [row]);
              }
        }
      />
      {actions.dialogs}
    </div>
  );
}

function BackToCampaigns({ slug }: { slug: string }) {
  return (
    <div className="flex items-center text-sm">
      <Button variant="ghost" size="sm" asChild className="-ml-2 h-7 text-slate-500 hover:text-slate-900">
        <Link to={campaignsPath(slug)}>
          <ArrowLeft className="h-3.5 w-3.5" />
          Campaigns
        </Link>
      </Button>
    </div>
  );
}

/** The former campaign page (`/email/campaigns/:id`, `?view=canvas`) — and
 *  any unknown tab — opens its Sequence. */
export function WorkspaceIndexRedirect() {
  const { slug, campaignId } = useParams<{ slug: string; campaignId: string }>();
  const location = useLocation();
  if (!slug || !campaignId) return null;
  return <Navigate to={`${emailBase(slug)}/campaigns/${campaignId}/${workspaceIndexTarget(location.search)}`} replace />;
}
